import { Module, Mutation, VuexModule } from "vuex-module-decorators";

import { platformKeyCookieKey } from "~/constants/cookieKeys";
import { store } from "~/store";
import type { PlatformConfiguration } from "~/types/PlatformConfiguration";
import { getFontFaceDefinitions } from "~/utils/getFontFaceDefinition";

@Module({
  name: "PlatformModule",
  stateFactory: true,
  namespaced: true,
  dynamic: true,
  store,
})
export default class PlatformModule extends VuexModule {
  host: string | undefined;
  platformConfiguration: PlatformConfiguration | undefined;
  platformIsInMaintenanceMode = false;

  @Mutation
  setPlatformConfiguration(platformConfiguration: PlatformConfiguration) {
    this.platformConfiguration = platformConfiguration;

    const runtimeConfig = useRuntimeConfig();
    if (runtimeConfig.public.nodeEnv === "test") return;

    const platformKeyCookie = useCookie<string>(platformKeyCookieKey);
    platformKeyCookie.value = platformConfiguration.platformId;
  }

  @Mutation
  setPlatformIsInMaintenanceMode(isInMaintenaceMode: boolean) {
    this.platformIsInMaintenanceMode = isInMaintenaceMode;
  }

  /**
   * Gets the css font face definitions for the platform specific fonts
   */
  get getPlatformFontDefinitions() {
    return this.getPlatformConfiguration.styling.fonts &&
      this.getPlatformConfiguration.styling.fonts?.length > 0
      ? getFontFaceDefinitions(
          this.getPlatformConfiguration.styling.fonts,
          "platform-font",
        )
      : null;
  }

  get getPlatformConfiguration() {
    if (this.platformConfiguration) return this.platformConfiguration;

    let errorMessage =
      "Trying to get the platform configuration before it was defined.";

    if (this.platformIsInMaintenanceMode) {
      errorMessage +=
        "\nThe platform fetch has failed due to maintenance mode.";
    }

    throw new Error(errorMessage);
  }

  /**
   * Attempts to retrieve the platform configuration from the Vuex store.
   *
   * Unlike `getPlatformConfiguration`, this function does not throw an error if the platform configuration
   * cannot be found. This is useful in plugins and middlewares where a missing platform configuration
   * should not result in additional errors.
   */
  get tryGetPlatformConfiguration() {
    return this.platformConfiguration;
  }

  get isFrameTheGame() {
    let isFTG: boolean;

    try {
      isFTG = this.getPlatformName === "Frame The Game";
    } catch {
      isFTG = false;
    }

    return isFTG;
  }

  get isMatchWornShirt() {
    let isMWS: boolean;

    try {
      isMWS = this.getPlatformName === "MatchWornShirt";
    } catch {
      isMWS = false;
    }

    return isMWS;
  }

  get getPlatformIsInMaintenanceMode() {
    return this.platformIsInMaintenanceMode;
  }

  get getFavIconLinks() {
    if (!this.platformConfiguration) {
      throw new Error(
        "Trying to get the platform configuration before it was defined",
      );
    }

    if (
      this.platformConfiguration.assets.favIconUrl === null ||
      this.platformConfiguration.assets.favIconUrl === ""
    ) {
      return [];
    }

    return [
      {
        rel: "icon",
        type: "image/x-icon",
        href: `${this.platformConfiguration.assets.favIconUrl}?auto=format,compress`,
      },
      {
        rel: "icon",
        type: "image/png",
        href: `${this.platformConfiguration.assets.favIconUrl}?auto=format,compress`,
      },
      {
        rel: "icon",
        sizes: "16x16",
        href: `${this.platformConfiguration.assets.favIconUrl}?auto=format,compress&w=16&h=16`,
      },
      {
        rel: "icon",
        sizes: "32x32",
        href: `${this.platformConfiguration.assets.favIconUrl}?auto=format,compress&w=32&h=32`,
      },
      {
        rel: "icon",
        sizes: "48x48",
        href: `${this.platformConfiguration.assets.favIconUrl}?auto=format,compress&w=48&h=48`,
      },
      {
        rel: "icon",
        sizes: "96x96",
        href: `${this.platformConfiguration.assets.favIconUrl}?auto=format,compress&w=96&h=96`,
      },
      {
        rel: "apple-touch-icon",
        sizes: "180x180",
        href: `${this.platformConfiguration.assets.favIconUrl}?auto=format,compress&w=180&h=180`,
      },
      {
        rel: "icon",
        sizes: "192x192",
        href: `${this.platformConfiguration.assets.favIconUrl}?auto=format,compress&w=192&h=192`,
      },
      {
        rel: "icon",
        sizes: "512x512",
        href: `${this.platformConfiguration.assets.favIconUrl}?auto=format,compress&w=512&h=512`,
      },
    ];
  }

  get getDefaultCurrency() {
    return this.getPlatformConfiguration.properties.defaultCurrency;
  }

  /**
   * Juventus wants to show MatchWornShirt on the tab title
   * TODO fix this properly. see: // TODO find a proper solution for this (see: https://www.notion.so/matchwornshirt/As-a-Whitelabel-partner-I-want-to-it-to-be-clear-to-the-customers-that-the-website-is-a-MWS-website-5168289492584e118b5d44604a79232a?pvs=4)
   */
  get getPlatformName() {
    return this.getPlatformConfiguration.platformName === "Juventus"
      ? "MatchWornShirt"
      : this.getPlatformConfiguration.platformName;
  }

  get getCustomerSupportLink() {
    return this.isFrameTheGame
      ? "https://support.framethegame.com"
      : "https://support.mws.com";
  }
}
